// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { supabase } from '../supabase';
import {
  isPasskeySupported,
  isBiometricAvailable,
  authenticateWithPasskey,
  hasLocalPasskeys,
} from '../webauthn';

export default function LoginPage() {
  const { signIn, signInWithToken, demoBypass } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [biometricReady, setBiometricReady] = useState(false);
  const [hasPasskeys, setHasPasskeys] = useState(false);
  const [autoAuthChecked, setAutoAuthChecked] = useState(false);

  // On mount: check for demo token in URL + check biometric availability
  useEffect(() => {
    // 1. Check for demo token (cross-app auth)
    const params = new URLSearchParams(window.location.search);
    const demoToken = params.get('demo_token');
    if (demoToken && signInWithToken) {
      // Clean URL immediately
      window.history.replaceState({}, '', window.location.pathname);
      handleDemoToken(demoToken);
      return;
    }

    // 2. Check biometric availability
    checkBiometric();
    setAutoAuthChecked(true);
  }, []);

  async function checkBiometric() {
    const supported = isPasskeySupported();
    const available = supported && await isBiometricAvailable();
    const hasKeys = hasLocalPasskeys();
    setBiometricReady(available);
    setHasPasskeys(hasKeys);
  }

  // Handle demo token auto-login
  async function handleDemoToken(token) {
    setLoading(true);
    setError('');
    try {
      if (signInWithToken) {
        const result = await signInWithToken(token);
        if (result?.success) {
          navigate('/');
          return;
        }
      }
      // Fallback: validate token directly
      const { data, error: fetchErr } = await supabase
        .from('demo_tokens')
        .select('*')
        .eq('token', token)
        .eq('used', false)
        .single();

      if (fetchErr || !data || new Date(data.expires_at) < new Date()) {
        setError('Demo link expired or invalid. Please sign in manually.');
        setAutoAuthChecked(true);
        setLoading(false);
        return;
      }

      // Mark used
      await supabase.from('demo_tokens').update({ used: true }).eq('id', data.id);

      // Bypass auth with demo role
      if (demoBypass) {
        demoBypass(data.user_email, data.user_role);
        navigate('/');
      }
    } catch (err) {
      setError('Demo link failed. Sign in manually.');
      setAutoAuthChecked(true);
    } finally {
      setLoading(false);
    }
  }

  // Fingerprint / FaceID login
  async function handleBiometricLogin() {
    setError('');
    setLoading(true);
    try {
      const result = await authenticateWithPasskey({ supabase });
      if (!result.success) {
        setError(result.error);
        setLoading(false);
        return;
      }

      // We got the email from passkey — now sign in with stored session
      // The passkey proves identity, but we need a Supabase session.
      // Check if there's an active session first
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        // Already authenticated — just navigate
        navigate('/');
        return;
      }

      // No active session — user needs to enter password once to create session
      // But we can pre-fill their email
      setEmail(result.email);
      setError('Fingerprint verified! Enter your password once to start a session. Next time you won\'t need it.');
      setLoading(false);
    } catch (err) {
      setError('Biometric login failed. Use password instead.');
      setLoading(false);
    }
  }

  // Standard email/password login
  async function handleLogin(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email, password);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Invalid login credentials');
    } finally {
      setLoading(false);
    }
  }

  const handleDemo = () => {
    if (demoBypass) demoBypass();
    navigate('/');
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px', background: 'var(--bg)',
    border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)',
    fontSize: 14, outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg)', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{
        width: '100%', maxWidth: 420, padding: 40, background: 'var(--card)',
        borderRadius: 16, border: '1px solid var(--border)', boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
      }}>
        {/* Brand Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 64, height: 64, borderRadius: 16, background: 'var(--primary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px', fontSize: 28, fontWeight: 700, color: '#fff'
          }}>
            {('__BRAND_NAME__').charAt(0)}
          </div>
          <h1 style={{ color: 'var(--text)', fontSize: 24, fontWeight: 700, margin: '0 0 4px' }}>
            __BRAND_NAME__
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: 0 }}>
            Sign in to your account
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            padding: '10px 14px', marginBottom: 20, borderRadius: 8,
            background: error.includes('verified') ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
            border: `1px solid ${error.includes('verified') ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
            color: error.includes('verified') ? '#10b981' : '#ef4444',
            fontSize: 13
          }}>
            {error}
          </div>
        )}

        {/* Biometric Login Button — shows if passkeys are registered on this device */}
        {biometricReady && hasPasskeys && (
          <>
            <button
              onClick={handleBiometricLogin}
              disabled={loading}
              style={{
                width: '100%', padding: '14px 0', background: 'var(--primary)', color: '#fff',
                border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
                marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              }}
            >
              <span style={{ fontSize: 22 }}>👆</span>
              {loading ? 'Scanning...' : 'Sign in with Fingerprint'}
            </button>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, margin: '16px 0',
              color: 'var(--text-muted)', fontSize: 12,
            }}>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              <span>or use password</span>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            </div>
          </>
        )}

        {/* Email/Password Form */}
        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                style={{ ...inputStyle, paddingRight: 44 }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                  fontSize: 18, color: 'var(--text-muted)', display: 'flex', alignItems: 'center',
                }}
              >
                {showPassword ? '\uD83D\uDE48' : '\uD83D\uDC41'}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%', padding: '12px 0', background: 'var(--primary)', color: '#fff',
              border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
              marginBottom: 12,
            }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        {/* Links */}
        <div style={{ textAlign: 'center', marginTop: 10, display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
          <span onClick={() => window.location.href = '/forgot-password'} style={{ color: 'var(--text-muted)', cursor: 'pointer' }}>
            Forgot password?
          </span>
          <span onClick={() => window.location.href = '/signup'} style={{ color: 'var(--primary)', cursor: 'pointer', fontWeight: 500 }}>
            Create Account
          </span>
        </div>

        {/* Presentation Mode */}
        <button
          onClick={handleDemo}
          style={{
            width: '100%', padding: '10px 0', background: 'transparent', marginTop: 16,
            border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-muted)',
            fontSize: 13, cursor: 'pointer', fontWeight: 500,
          }}
        >
          Presentation Mode
        </button>

        <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 11, marginTop: 24, marginBottom: 0 }}>
          Powered by Miskulin Technologies
        </p>
      </div>
    </div>
  );
}
